import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { HERO_STACKS as MOCK_STACKS } from '../mock/products';
import { useApp } from '../context/AppContext';
import { useCurrency } from '../context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HeroCarousel = () => {
  const { fmt } = useCurrency();
  const [stacks, setStacks] = useState(MOCK_STACKS);
  const [i, setI] = useState(0);
  const { addToCart } = useApp();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/stacks`);
        if (!cancelled && Array.isArray(data) && data.length > 0) setStacks(data);
      } catch { /* keep mock */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const stack = stacks[Math.min(i, stacks.length - 1)] || MOCK_STACKS[0];

  useEffect(() => {
    if (stacks.length <= 1) return;
    const t = setInterval(() => setI(n => (n + 1) % stacks.length), 6500);
    return () => clearInterval(t);
  }, [stacks.length]);

  const prev = () => setI(n => (n - 1 + stacks.length) % stacks.length);
  const next = () => setI(n => (n + 1) % stacks.length);

  const handleAdd = () => addToCart({
    id: `stack-${stack.id}`,
    name: stack.title,
    strength: 'Bundle',
    packaging: `${stack.items.length} items`,
    price: stack.price,
    image: stack.image,
    catLabel: stack.kicker,
  });

  return (
    <section className="max-w-7xl mx-auto px-6 pt-8">
      <div className="relative overflow-hidden rounded-2xl bg-neutral-900">
        <div className="absolute inset-0">
          <img src={stack.image} alt={stack.title} className="w-full h-full object-cover opacity-90" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10" />
        </div>

        <div className="relative px-8 md:px-14 py-14 md:py-20">
          <div className="flex items-center gap-2 mb-6">
            <span className="bg-gradient-to-r from-[hsl(var(--brand))] to-[hsl(var(--brand-strong))] text-white text-[11px] tracking-[0.18em] px-3 py-1 rounded-full uppercase font-semibold shadow-lg shadow-black/20">{stack.kicker}</span>
            <span className="text-white/80 text-[11px] tracking-[0.2em] uppercase">{stack.tag}</span>
          </div>

          <h1 className="font-serif text-white text-[46px] md:text-[64px] leading-[0.98] mb-3 tracking-tight">{stack.title}</h1>
          <p className="text-white/80 text-[15px] md:text-[16px] mb-6 max-w-xl">{stack.subtitle}</p>

          <div className="flex flex-wrap gap-2 max-w-2xl mb-7">
            {stack.items.map((it, idx) => (
              <span key={idx} className="bg-black/40 backdrop-blur text-white/90 text-[12.5px] px-3 py-1.5 rounded-full border border-white/10">{it}</span>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-6">
            <span className="text-white text-[26px] font-medium">{fmt(stack.price)}</span>
          </div>

          <button onClick={handleAdd} className="btn-brand text-[14px] font-semibold rounded-full px-6 py-3 tracking-wide">
            Add Stack — {fmt(stack.price)}
          </button>
        </div>

        <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white grid place-items-center transition-colors">
          <ChevronLeft size={18} />
        </button>
        <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white grid place-items-center transition-colors">
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="flex justify-center gap-1.5 mt-4">
        {stacks.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)} className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-8 bg-brand' : 'w-1.5 bg-neutral-300 dark:bg-neutral-700 hover:bg-neutral-400 dark:hover:bg-neutral-600'}`} />
        ))}
      </div>
    </section>
  );
};

export default HeroCarousel;
